import React, { useCallback, useEffect, useRef, useState } from "react";
import HighlightedText from "shared/HighlightedText";
import AppSelect from "shared/AppSelect";
import { ChevronRight, Eye, GitCompare, Pencil, Printer, Save, Sparkles, X } from "lucide-react";
import {
  CvDocument,
  SectionKey,
  cvApiBase,
  listDocuments,
  getLastEnhancement,
  createDocument,
} from "features/cv-processor/api/CvProcessorApi";
import {
  CvPreview,
  EducationEditor,
  ProjectsEditor,
  ScoreBadge,
  SECTION_KEYS,
  SECTION_LABELS,
  SECTION_PLACEHOLDERS,
  SkillsEditor,
  WorkExperienceEditor,
  buildPrintHtml,
  getDocSectionText,
  openPrintWindow,
  sectionToApiPayload,
} from "features/cv-processor/CvProcessorPage";
import SaveAsNewCvModal from "features/cv-processor/ui/SaveAsNewCvModal";
import {
  CoverLetterDocument,
  CoverLetterEnhanceResult,
  listCoverLetterDocuments,
  enhanceCoverLetterPreview,
  getLastCoverLetterEnhancement,
  createCoverLetterDocument,
} from "features/cover-letter/api/CoverLetterApi";
import { buildCoverLetterPrintHtml } from "features/cover-letter/CoverLetterPage";
import SaveAsNewCoverLetterModal from "features/cover-letter/ui/SaveAsNewCoverLetterModal";
import { diffWords } from "diff";
import { Link } from "react-router-dom";
import { LogPanel, LogEvent, TokenUsage } from "features/cv-processor/ui/LogPanel";

function Spinner({ size = "md" }: { size?: "sm" | "md" }) {
  const cls = size === "sm" ? "w-4 h-4" : "w-6 h-6";
  return (
    <div
      className={`${cls} border-2 border-primary border-t-transparent rounded-full animate-spin`}
    />
  );
}



// ---------------------------------------------------------------------------
// CV Comparison Modal
// ---------------------------------------------------------------------------

function DiffText({
  original,
  enhanced,
  side,
}: {
  original: string;
  enhanced: string;
  side: "left" | "right";
}) {
  const changes = React.useMemo(
    () => diffWords(original || "", enhanced || ""),
    [original, enhanced]
  );
  return (
    <div className="text-sm text-zinc-700 leading-relaxed whitespace-pre-wrap break-words" style={{ fontFamily: '"EB Garamond", Garamond, "Adobe Garamond Pro", Georgia, serif', fontSize: "0.9rem", lineHeight: "1.7" }}>
      {changes.map((part, i) => {
        if (side === "left") {
          if (part.added) return null;
          if (part.removed)
            return (
              <span key={i} className="bg-red-100 text-red-700 line-through decoration-red-400">
                {part.value}
              </span>
            );
          return <React.Fragment key={i}>{part.value}</React.Fragment>;
        } else {
          if (part.removed) return null;
          if (part.added)
            return (
              <span key={i} className="bg-green-100 text-green-700">
                {part.value}
              </span>
            );
          return <React.Fragment key={i}>{part.value}</React.Fragment>;
        }
      })}
    </div>
  );
}


export function ComparisonModal({
  originalDoc,
  enhancedDoc,
  jobDescription,
  onClose,
}: {
  originalDoc: CvDocument;
  enhancedDoc: CvDocument;
  jobDescription: string;
  onClose: () => void;
}) {
  const [jdOpen, setJdOpen] = useState(false);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKey);
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[1000000] flex flex-col bg-surface"
      role="dialog"
      aria-modal="true"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="shrink-0 flex items-center justify-between px-6 py-4 border-b border-border">
        <div className="flex items-center gap-2.5">
          <GitCompare className="w-4 h-4 text-primary" />
          <h2 className="text-sm font-semibold text-foreground font-inter">
            Side-by-Side Comparison
          </h2>
          <span className="text-xs text-muted-foreground font-inter">— {originalDoc.title}</span>
        </div>
        <button
          onClick={onClose}
          className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-surface-muted transition-colors"
          aria-label="Close comparison"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Job Description collapsible */}
      {jobDescription && (
        <div className="shrink-0 border-b border-border">
          <button
            onClick={() => setJdOpen((v) => !v)}
            className="w-full flex items-center gap-2 px-6 py-2.5 text-xs font-medium text-muted-foreground hover:text-foreground font-inter transition-colors"
          >
            <ChevronRight
              className={`w-3.5 h-3.5 shrink-0 transition-transform ${jdOpen ? "rotate-90" : ""}`}
            />
            Job Description
            {!jdOpen && (
              <span className="ml-1 text-muted-foreground truncate max-w-lg font-mono">
                {jobDescription.slice(0, 120).replace(/\n/g, " ")}…
              </span>
            )}
          </button>
          {jdOpen && (
            <div className="px-6 pb-4 bg-surface/40">
              <pre className="text-xs text-foreground font-inter whitespace-pre-wrap leading-relaxed max-h-52 overflow-y-auto">
                <HighlightedText text={jobDescription} />
              </pre>
            </div>
          )}
        </div>
      )}

      {/* Column headers */}
      <div className="shrink-0 flex border-b border-zinc-300 bg-zinc-100">
        <div className="w-1/2 px-8 py-2 border-r border-zinc-300">
          <span className="text-[10px] font-semibold text-zinc-500 font-inter uppercase tracking-widest">
            Original
          </span>
        </div>
        <div className="w-1/2 px-8 py-2">
          <span className="text-[10px] font-semibold text-zinc-500 font-inter uppercase tracking-widest">
            Enhanced
          </span>
        </div>
      </div>

      {/* Scrollable section diffs */}
      <div className="flex-1 overflow-y-auto bg-zinc-100">
        {SECTION_KEYS.map((key) => {
          const originalText = getDocSectionText(originalDoc, key);
          const enhancedText = getDocSectionText(enhancedDoc, key);
          const hasChanges = originalText.trim() !== enhancedText.trim();
          return (
            <div key={key} className="border-b border-zinc-200 last:border-b-0">
              {/* Section label — matches CvPreview's section heading style */}
              <div className="flex items-center gap-3 px-8 py-2 bg-zinc-200/60 border-b border-zinc-300 sticky top-0 z-10">
                <span className="text-xs font-bold tracking-widest text-zinc-800 uppercase" style={{ fontFamily: '"EB Garamond", Garamond, "Adobe Garamond Pro", Georgia, serif' }}>
                  {SECTION_LABELS[key]}
                </span>
                {hasChanges ? (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-mono border border-emerald-300">
                    modified
                  </span>
                ) : (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-400 font-mono border border-zinc-300">
                    unchanged
                  </span>
                )}
              </div>
              {/* Side-by-side */}
              <div className="flex divide-x divide-zinc-200 min-h-[60px] bg-white">
                <div className="w-1/2 px-8 py-5">
                  {originalText.trim() ? (
                    <DiffText original={originalText} enhanced={enhancedText} side="left" />
                  ) : (
                    <p className="text-xs text-zinc-400 italic font-inter">No content</p>
                  )}
                </div>
                <div className="w-1/2 px-8 py-5">
                  {enhancedText.trim() ? (
                    <DiffText original={originalText} enhanced={enhancedText} side="right" />
                  ) : (
                    <p className="text-xs text-zinc-400 italic font-inter">No content</p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="shrink-0 flex items-center gap-6 px-6 py-3 border-t border-border bg-surface">
        <span className="text-[10px] text-muted-foreground font-inter uppercase tracking-wide">
          Legend:
        </span>
        <div className="flex items-center gap-1.5">
          <span className="inline-block w-2.5 h-2.5 rounded-sm bg-red-100 border border-red-300" />
          <span className="text-[10px] text-muted-foreground font-inter line-through decoration-red-400">
            removed
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="inline-block w-2.5 h-2.5 rounded-sm bg-green-100 border border-green-300" />
          <span className="text-[10px] text-muted-foreground font-inter">added</span>
        </div>
        <span className="ml-auto text-[10px] text-muted-foreground font-mono">Esc to close</span>
      </div>
    </div>
  );
}

export default function EnhancementsPage() {
  const [docs, setDocs] = useState<CvDocument[]>([]);
  const [selectedDocId, setSelectedDocId] = useState<number | "">("");
  const [jobDescription, setJobDescription] = useState("");
  const [enhancing, setEnhancing] = useState(false);
  const [enhancedDoc, setEnhancedDoc] = useState<CvDocument | null>(null);
  const [error, setError] = useState("");
  const [loadingDocs, setLoadingDocs] = useState(true);
  const [logs, setLogs] = useState<LogEvent[]>([]);
  const [tokenUsage, setTokenUsage] = useState<TokenUsage | null>(null);
  const logsEndRef = useRef<HTMLDivElement>(null);
  const [showPrevious, setShowPrevious] = useState(false);
  // Provider is a global, admin-controlled setting (Settings -> LLM Provider
  // Selection) — this page only displays whichever one actually ran, it
  // doesn't let the user pick.
  const [runProvider, setRunProvider] = useState<"openai" | "deepseek" | null>(null);
  const [loadingPrevious, setLoadingPrevious] = useState(false);
  const [noPreviousFound, setNoPreviousFound] = useState(false);
  const [showComparison, setShowComparison] = useState(false);
  const [upworkMode, setUpworkMode] = useState(false);
  const [showSaveCvModal, setShowSaveCvModal] = useState(false);
  const [saveCvMessage, setSaveCvMessage] = useState("");
  const previousFetchDocRef = useRef<number | null>(null);
  const draftJobDescriptionRef = useRef("");

  // Section editor state (post-enhancement, in-memory only)
  const [localContent, setLocalContent] = useState<Record<SectionKey, string>>(
    {} as Record<SectionKey, string>
  );
  const [activeSection, setActiveSection] = useState<SectionKey>("headline");
  const [activeView, setActiveView] = useState<"edit" | "preview">("edit");
  const [activeTab, setActiveTab] = useState<"cv" | "coverLetter">("cv");

  // ── Cover Letter tailoring — independent action, shares the Job Description
  // textarea above with the CV flow (same job, same materials). One fast LLM
  // call, no streaming/logs, grounded in whichever CV is currently selected. ──
  const [coverLetterDocs, setCoverLetterDocs] = useState<CoverLetterDocument[]>([]);
  const [loadingCoverLetterDocs, setLoadingCoverLetterDocs] = useState(true);
  const [selectedCoverLetterId, setSelectedCoverLetterId] = useState<number | "">("");
  const [clEnhancing, setClEnhancing] = useState(false);
  const [clResult, setClResult] = useState<CoverLetterEnhanceResult | null>(null);
  const [clLocalContent, setClLocalContent] = useState("");
  const [clError, setClError] = useState("");
  const [clShowPrevious, setClShowPrevious] = useState(false);
  const [clLoadingPrevious, setClLoadingPrevious] = useState(false);
  const [clNoPreviousFound, setClNoPreviousFound] = useState(false);
  const clPreviousFetchDocRef = useRef<number | null>(null);
  const [showSaveClModal, setShowSaveClModal] = useState(false);
  const [saveClMessage, setSaveClMessage] = useState("");

  useEffect(() => {
    listDocuments()
      .then((data) => {
        setDocs(data);
        if (data.length > 0) setSelectedDocId(data[0].id);
        setLoadingDocs(false);
      })
      .catch(() => setLoadingDocs(false));
  }, []);

  useEffect(() => {
    listCoverLetterDocuments()
      .then((data) => {
        setCoverLetterDocs(data);
        if (data.length > 0) setSelectedCoverLetterId(data[0].id);
        setLoadingCoverLetterDocs(false);
      })
      .catch(() => setLoadingCoverLetterDocs(false));
  }, []);

  // When a new cover letter result arrives, seed the editable preview from it.
  useEffect(() => {
    if (clResult) setClLocalContent(clResult.content);
  }, [clResult]);

  // When a new enhanced doc arrives, seed localContent from it and reset view
  useEffect(() => {
    if (!enhancedDoc) return;
    setLocalContent(
      SECTION_KEYS.reduce((acc, key) => {
        acc[key] = getDocSectionText(enhancedDoc, key);
        return acc;
      }, {} as Record<SectionKey, string>)
    );
    setActiveSection("headline");
    setActiveView("edit");
  }, [enhancedDoc]);

  // Auto-scroll logs panel
  useEffect(() => {
    logsEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [logs]);

  // Derived doc: enhancedDoc base with any in-memory edits applied
  // localContent holds plain-text strings; CvPreview / buildPrintHtml handle both via getDocSectionText
  const previewDoc: CvDocument | null = enhancedDoc
    ? { ...enhancedDoc, ...(localContent as any) }
    : null;

  const originalDoc = docs.find((d) => d.id === selectedDocId) ?? null;

  const handleCloseComparison = useCallback(() => setShowComparison(false), []);

  const handleDocChange = (idStr: string) => {
    const id = idStr === "" ? "" : Number(idStr);
    setSelectedDocId(id);
    setEnhancedDoc(null);
    setError("");
    setLogs([]);
    setTokenUsage(null);
    setShowPrevious(false);
    setNoPreviousFound(false);
    setShowComparison(false);
    setSaveCvMessage("");
    previousFetchDocRef.current = null;
  };

  const handleTogglePrevious = async (on: boolean) => {
    setShowPrevious(on);
    setNoPreviousFound(false);
    setError("");
    setRunProvider(null);
    setSaveCvMessage("");
    if (!on) {
      setEnhancedDoc(null);
      // Restore whatever the user had typed before toggling "Load previous" on,
      // instead of blanking it — this field is now shared with the Cover Letter
      // section below, so clearing it disables that section with no explanation.
      setJobDescription(draftJobDescriptionRef.current);
      setLogs([]);
      setShowComparison(false);
      return;
    }
    if (!selectedDocId) return;
    draftJobDescriptionRef.current = jobDescription;
    const fetchingForDoc = selectedDocId as number;
    previousFetchDocRef.current = fetchingForDoc;
    setLoadingPrevious(true);
    try {
      const data = await getLastEnhancement(fetchingForDoc);
      if (previousFetchDocRef.current !== fetchingForDoc) return;
      setJobDescription(data.job_description);
      setEnhancedDoc(data.result);
      const loadedLogs = data.logs as LogEvent[];
      setLogs(loadedLogs);
      const usageEvent = loadedLogs.find((e) => e.type === "token_usage");
      setTokenUsage(usageEvent ? { prompt_tokens: usageEvent.prompt_tokens ?? 0, completion_tokens: usageEvent.completion_tokens ?? 0, total_tokens: usageEvent.total_tokens ?? 0, cache_hit_tokens: usageEvent.cache_hit_tokens ?? 0 } : null);
      setRunProvider((data.provider as "openai" | "deepseek") ?? null);
    } catch (e: any) {
      if (previousFetchDocRef.current !== fetchingForDoc) return;
      if (e?.response?.status === 404) {
        setNoPreviousFound(true);
      } else {
        setError("Failed to load previous enhancement. Please try again.");
        setShowPrevious(false);
      }
    } finally {
      setLoadingPrevious(false);
    }
  };

  const handleEnhance = async () => {
    if (!selectedDocId || !jobDescription.trim()) return;
    setEnhancing(true);
    setRunProvider(null);
    setError("");
    setNoPreviousFound(false);
    setEnhancedDoc(null);
    setLogs([]);
    setTokenUsage(null);
    setShowComparison(false);
    setSaveCvMessage("");

    const token = localStorage.getItem("token");
    const apiBase = process.env.REACT_APP_API_URL || "http://localhost:8000";

    try {
      const response = await fetch(
        `${apiBase}${cvApiBase()}/documents/${selectedDocId}/enhance-all-preview-stream`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ job_description: jobDescription.trim() }),
        }
      );

      if (!response.ok) {
        const detail = await response.json().catch(() => ({}));
        throw new Error((detail as any)?.detail || `Request failed: ${response.status}`);
      }

      if (!response.body) {
        throw new Error("Streaming is not supported in this browser or network environment. Try a different browser.");
      }
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      const processLine = (line: string) => {
        if (!line.startsWith("data: ")) return;
        const raw = line.slice(6).trim();
        if (!raw) return;
        try {
          const event = JSON.parse(raw);
          if (event.type === "provider") {
            if (event.provider === "openai" || event.provider === "deepseek") setRunProvider(event.provider);
          } else if (event.type === "complete") {
            setEnhancedDoc(event.result);
            if (event.provider === "openai" || event.provider === "deepseek") setRunProvider(event.provider);
          } else if (event.type === "token_usage") {
            setTokenUsage({ prompt_tokens: event.prompt_tokens ?? 0, completion_tokens: event.completion_tokens ?? 0, total_tokens: event.total_tokens ?? 0, cache_hit_tokens: event.cache_hit_tokens ?? 0 });
          } else if (event.type === "error") {
            setError(event.message || "Enhancement failed.");
            if (event.provider === "openai" || event.provider === "deepseek") setRunProvider(event.provider);
            setLogs((prev) => [...prev, event as LogEvent]);
          } else {
            setLogs((prev) => [...prev, event as LogEvent]);
          }
        } catch {}
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() ?? "";
        for (const line of lines) processLine(line);
      }

      // Flush any multi-byte sequences held by the decoder and process any
      // remaining partial line that arrived without a trailing newline.
      buffer += decoder.decode();
      if (buffer) processLine(buffer);
    } catch (e: any) {
      setError(e.message || "Enhancement failed. Please try again.");
    } finally {
      setEnhancing(false);
    }
  };

  const handlePrint = () => {
    if (!previewDoc) return;
    openPrintWindow(buildPrintHtml(previewDoc, upworkMode));
  };

  const handleSaveCvAsNew = async (title: string, categoryId: number | null) => {
    if (!previewDoc || !enhancedDoc) return;
    await createDocument({
      title,
      category_id: categoryId,
      job_description: jobDescription,
      headline: localContent.headline,
      summary: localContent.summary,
      work_experience: sectionToApiPayload("work_experience", localContent.work_experience),
      skills: sectionToApiPayload("skills", localContent.skills),
      education: sectionToApiPayload("education", localContent.education),
      projects: sectionToApiPayload("projects", localContent.projects),
      header_info: enhancedDoc.header_info,
    });
    setShowSaveCvModal(false);
    setSaveCvMessage("Saved as a new CV — find it under My CVs.");
    listDocuments().then(setDocs).catch(() => {});
  };

  const handleCoverLetterDocChange = (idStr: string) => {
    const id = idStr === "" ? "" : Number(idStr);
    setSelectedCoverLetterId(id);
    setClResult(null);
    setClError("");
    setClShowPrevious(false);
    setClNoPreviousFound(false);
    setSaveClMessage("");
    clPreviousFetchDocRef.current = null;
  };

  const handleToggleClPrevious = async (on: boolean) => {
    setClShowPrevious(on);
    setClNoPreviousFound(false);
    setClError("");
    setSaveClMessage("");
    if (!on) {
      setClResult(null);
      return;
    }
    if (!selectedCoverLetterId) return;
    const fetchingForDoc = selectedCoverLetterId as number;
    clPreviousFetchDocRef.current = fetchingForDoc;
    setClLoadingPrevious(true);
    try {
      const data = await getLastCoverLetterEnhancement(fetchingForDoc);
      if (clPreviousFetchDocRef.current !== fetchingForDoc) return;
      // Deliberately not touching the shared Job Description textarea here — it's
      // shared with the CV flow above, and this toggle is just for reviewing a past
      // cover-letter run, not for re-seeding the page's current job context.
      if (data.result) {
        setClResult(data.result);
      } else {
        setClNoPreviousFound(true);
      }
    } catch (e: any) {
      if (clPreviousFetchDocRef.current !== fetchingForDoc) return;
      if (e?.response?.status === 404) {
        setClNoPreviousFound(true);
      } else {
        setClError("Failed to load previous enhancement. Please try again.");
        setClShowPrevious(false);
      }
    } finally {
      setClLoadingPrevious(false);
    }
  };

  const handleEnhanceCoverLetter = async () => {
    if (!selectedCoverLetterId || !jobDescription.trim()) return;
    setClEnhancing(true);
    setClError("");
    setClNoPreviousFound(false);
    setClResult(null);
    setSaveClMessage("");
    try {
      const result = await enhanceCoverLetterPreview(
        selectedCoverLetterId as number,
        jobDescription.trim(),
        selectedDocId === "" ? null : selectedDocId,
      );
      setClResult(result);
    } catch (e: any) {
      setClError(e.response?.data?.detail || "Enhancement failed. Please try again.");
    } finally {
      setClEnhancing(false);
    }
  };

  const handleClPrint = () => {
    if (!clResult) return;
    const doc = coverLetterDocs.find((d) => d.id === selectedCoverLetterId);
    openPrintWindow(buildCoverLetterPrintHtml({
      id: 0, owner_type: "", owner_id: 0,
      title: doc?.title || "Cover Letter",
      content: clLocalContent,
      category_id: null, last_score: null, last_enhanced_at: null, created_at: "", updated_at: "",
    }));
  };

  const handleSaveClAsNew = async (title: string, categoryId: number) => {
    if (!clResult) return;
    await createCoverLetterDocument({ title, category_id: categoryId, content: clLocalContent });
    setShowSaveClModal(false);
    setSaveClMessage("Saved as a new cover letter — find it under My Cover Letters.");
    listCoverLetterDocuments().then(setCoverLetterDocs).catch(() => {});
  };

  const handleContentChange = useCallback(
    (value: string) => {
      setLocalContent((prev) => ({ ...prev, [activeSection]: value }));
    },
    [activeSection]
  );

  const currentContent = localContent[activeSection] ?? "";

  const sectionHasContent = (key: SectionKey) =>
    !!(localContent[key] || "").trim();

  const overallScore = enhancedDoc?.holistic_review?.overall_score ?? null;
  const sectionScores = enhancedDoc?.last_section_scores ?? null;

  const activeSectionScore =
    sectionScores?.[activeSection]?.total_score ?? null;
  const activeSectionFix =
    sectionScores?.[activeSection]?.top_priority_fix ?? null;

  const showLogs = logs.length > 0 || enhancing;

  return (
    <div className="max-w-7xl">
      <div className={showLogs ? "flex gap-6 items-start" : ""}>
        {/* Left column: existing content */}
        <div className={showLogs ? "flex-1 min-w-0" : ""}>
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-foreground font-inter">
              Enhancements
            </h1>
            <p className="text-sm text-muted-foreground font-inter mt-1">
              Tailor a CV or cover letter for a specific job — results are
              preview-only and never alter the saved document.
            </p>
          </div>

          {/* Shared Job Description — used by both CV and Cover Letter tailoring below */}
          <div className="rounded-2xl border border-border bg-surface/50 p-5 mb-6">
            <label className="block text-xs font-medium text-muted-foreground font-inter mb-1.5">
              Job Description
            </label>
            <textarea
              value={jobDescription}
              onChange={(e) => setJobDescription(e.target.value)}
              disabled={showPrevious || clShowPrevious}
              placeholder="Paste the full job description here… (shared by both tabs below)"
              rows={8}
              className="w-full px-3 py-2.5 bg-surface-muted border border-border rounded-xl text-sm text-foreground font-inter placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none disabled:opacity-60 disabled:cursor-not-allowed"
            />
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-1 p-1 bg-surface-muted rounded-xl w-fit border border-border mb-6">
            <button
              onClick={() => setActiveTab("cv")}
              className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-medium font-inter rounded-lg transition-colors ${
                activeTab === "cv" ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <Sparkles className="w-3.5 h-3.5" />
              CV Enhancement
            </button>
            <button
              onClick={() => setActiveTab("coverLetter")}
              className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-medium font-inter rounded-lg transition-colors ${
                activeTab === "coverLetter" ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <Sparkles className="w-3.5 h-3.5" />
              Cover Letter Enhancement
            </button>
          </div>

          {activeTab === "cv" && (
          <>
          {/* Controls panel */}
          <div className="rounded-2xl border border-border bg-surface/50 p-5 mb-6 space-y-4">
            <div>
              <label className="block text-xs font-medium text-muted-foreground font-inter mb-1.5">
                Select CV
              </label>
              {loadingDocs ? (
                <div className="flex items-center gap-2 py-2">
                  <Spinner size="sm" />
                  <span className="text-sm text-muted-foreground font-inter">
                    Loading CVs…
                  </span>
                </div>
              ) : docs.length === 0 ? (
                <p className="text-sm text-muted-foreground font-inter">
                  No CVs found. Create one in My CVs first.
                </p>
              ) : (
                <AppSelect value={String(selectedDocId)} onChange={handleDocChange}>
                  <option value="">— Choose a CV —</option>
                  {docs.map((doc) => (
                    <option key={doc.id} value={doc.id}>
                      {doc.title}
                    </option>
                  ))}
                </AppSelect>
              )}
            </div>

            {selectedDocId !== "" && (
              <div className="flex items-center justify-between py-1">
                <div className="flex items-center gap-2.5">
                  <button
                    onClick={() => handleTogglePrevious(!showPrevious)}
                    disabled={loadingPrevious || enhancing}
                    className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${
                      showPrevious ? "bg-primary" : "bg-border"
                    } disabled:opacity-50`}
                    role="switch"
                    aria-checked={showPrevious}
                  >
                    <span
                      className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow transition-transform duration-200 ${
                        showPrevious ? "translate-x-4" : "translate-x-0"
                      }`}
                    />
                  </button>
                  <span className="text-xs text-muted-foreground font-inter">
                    {loadingPrevious
                      ? "Loading…"
                      : showPrevious
                      ? "Showing previous enhancement"
                      : "Load previous enhancement"}
                  </span>
                </div>
                {docs.find((d) => d.id === selectedDocId)?.last_enhanced_at && (
                  <span className="text-xs text-muted-foreground font-inter">
                    Last: {docs.find((d) => d.id === selectedDocId)!.last_enhanced_at}
                  </span>
                )}
              </div>
            )}

            {noPreviousFound && (
              <p className="text-xs text-muted-foreground font-inter">
                No previous enhancement saved for this CV.
              </p>
            )}

            {selectedDocId !== "" && !docs.find((d) => d.id === selectedDocId)?.category_id && (
              <div className="flex items-start gap-2.5 px-3 py-2.5 rounded-lg bg-amber-500/8 border border-amber-500/25">
                <span className="text-amber-400 text-sm mt-0.5">⚠</span>
                <div>
                  <p className="text-xs font-semibold text-amber-400 font-inter">No category assigned</p>
                  <p className="text-xs text-amber-400/70 font-inter mt-0.5">
                    Universal skills pool matching will be skipped. Assign a category to this CV in{" "}
                    <Link to={cvApiBase()} className="underline hover:text-amber-300">My CVs</Link>
                    {" "}to enable automatic skill gap filling.
                  </p>
                </div>
              </div>
            )}

            {error && (
              <p className="text-sm text-red-400 font-inter">{error}</p>
            )}

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <button
                  type="button"
                  onClick={() => setUpworkMode((v) => !v)}
                  className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${
                    upworkMode ? "bg-primary" : "bg-border"
                  }`}
                  role="switch"
                  aria-checked={upworkMode}
                >
                  <span
                    className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow transition-transform duration-200 ${
                      upworkMode ? "translate-x-4" : "translate-x-0"
                    }`}
                  />
                </button>
                <span className="text-xs text-muted-foreground font-inter">
                  Enhance for Upwork (omit contact info)
                </span>
              </div>
              <button
                onClick={handleEnhance}
                disabled={!selectedDocId || !jobDescription.trim() || enhancing || showPrevious}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed text-primary-foreground text-sm font-medium font-inter rounded-xl transition-colors"
              >
                {enhancing ? <Spinner size="sm" /> : <Sparkles className="w-4 h-4" />}
                {enhancing ? "Enhancing all sections…" : "Enhance CV"}
              </button>
            </div>
          </div>

          {/* Post-enhancement section editor */}
          {previewDoc && (
            <div>
              {/* Enhanced Result header */}
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="flex items-center gap-3">
                    <h2 className="text-base font-semibold text-foreground font-inter">
                      Enhanced Result
                    </h2>
                    {overallScore !== null && (
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs text-muted-foreground font-inter">ATS Score</span>
                        <ScoreBadge score={overallScore} size="md" />
                      </div>
                    )}
                  </div>
                  {enhancedDoc?.holistic_review?.top_priority_fix &&
                    enhancedDoc.holistic_review.top_priority_fix !== "none" && (
                      <p className="text-xs text-muted-foreground font-inter mt-1">
                        Top fix:{" "}
                        <span className="text-muted-foreground">
                          {enhancedDoc.holistic_review.top_priority_fix}
                        </span>
                      </p>
                    )}
                  <p className="text-xs text-muted-foreground font-inter mt-1">
                    Edit any section below — changes are preview-only unless saved as a new CV.
                  </p>
                  {saveCvMessage && (
                    <p className="text-xs text-emerald-400 font-inter mt-1">{saveCvMessage}</p>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => setShowSaveCvModal(true)}
                    className="inline-flex items-center gap-2 px-3 py-2 bg-surface-muted hover:bg-surface-muted border border-border text-foreground text-sm font-medium font-inter rounded-xl transition-colors"
                  >
                    <Save className="w-4 h-4" />
                    Save as new CV
                  </button>
                  <button
                    onClick={handlePrint}
                    className="inline-flex items-center gap-2 px-3 py-2 bg-surface-muted hover:bg-surface-muted border border-border text-foreground text-sm font-medium font-inter rounded-xl transition-colors"
                  >
                    <Printer className="w-4 h-4" />
                    Download / Print
                  </button>
                </div>
              </div>

              {/* Edit / Preview / Compare */}
              <div className="flex items-center gap-3 mb-4">
                <div className="flex items-center gap-1 p-1 bg-surface-muted rounded-xl w-fit border border-border">
                  <button
                    onClick={() => setActiveView("edit")}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium font-inter rounded-lg transition-colors ${
                      activeView === "edit"
                        ? "bg-surface text-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    <Pencil className="w-3.5 h-3.5" />
                    Edit
                  </button>
                  <button
                    onClick={() => setActiveView("preview")}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium font-inter rounded-lg transition-colors ${
                      activeView === "preview"
                        ? "bg-surface text-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    <Eye className="w-3.5 h-3.5" />
                    Preview
                  </button>
                </div>
                <button
                  onClick={() => setShowComparison(true)}
                  className="inline-flex items-center gap-1 text-xs font-inter text-muted-foreground hover:text-primary transition-colors"
                >
                  <GitCompare className="w-3 h-3" />
                  Compare
                </button>
              </div>

              {activeView === "preview" ? (
                <CvPreview doc={previewDoc} hideContactInfo={upworkMode} />
              ) : (
                <div className="flex gap-6">
                  {/* Section sidebar */}
                  <div className="w-52 shrink-0 space-y-1">
                    {SECTION_KEYS.map((key) => {
                      const isActive = activeSection === key;
                      const hasContent = sectionHasContent(key);
                      const sectionScore = sectionScores?.[key]?.total_score ?? null;
                      return (
                        <button
                          key={key}
                          onClick={() => setActiveSection(key)}
                          className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-inter transition-colors text-left ${
                            isActive
                              ? "bg-primary/10 text-primary border border-primary/30"
                              : "text-muted-foreground hover:text-foreground hover:bg-surface-muted border border-transparent"
                          }`}
                        >
                          <span className="flex-1 truncate">
                            {SECTION_LABELS[key]}
                          </span>
                          {sectionScore !== null ? (
                            <ScoreBadge score={sectionScore} />
                          ) : hasContent && !isActive ? (
                            <ChevronRight className="w-3.5 h-3.5 shrink-0 opacity-40" />
                          ) : null}
                        </button>
                      );
                    })}
                  </div>

                  {/* Section editor */}
                  <div className="flex-1 min-w-0">
                    <div className="rounded-2xl border border-border bg-surface/50 overflow-hidden">
                      <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                        <h2 className="text-sm font-semibold text-foreground font-inter">
                          {SECTION_LABELS[activeSection]}
                        </h2>
                        {activeSectionScore !== null && (
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground font-inter">ATS</span>
                            <ScoreBadge score={activeSectionScore} />
                          </div>
                        )}
                      </div>

                      {activeSectionFix && activeSectionFix !== "none" && (
                        <div className="px-4 py-2.5 border-b border-border bg-surface-muted">
                          <p className="text-xs text-muted-foreground font-inter">
                            <span className="text-muted-foreground">Top fix: </span>
                            {activeSectionFix}
                          </p>
                        </div>
                      )}

                      {activeSection === "work_experience" ? (
                        <WorkExperienceEditor
                          key="work_experience"
                          value={currentContent}
                          onChange={handleContentChange}
                          onBlur={() => {}}
                        />
                      ) : activeSection === "education" ? (
                        <EducationEditor
                          key="education"
                          value={currentContent}
                          onChange={handleContentChange}
                          onBlur={() => {}}
                        />
                      ) : activeSection === "projects" ? (
                        <ProjectsEditor
                          key="projects"
                          value={currentContent}
                          onChange={handleContentChange}
                          onBlur={() => {}}
                        />
                      ) : activeSection === "skills" ? (
                        <SkillsEditor
                          key="skills"
                          value={currentContent}
                          onChange={handleContentChange}
                          onBlur={() => {}}
                        />
                      ) : (
                        <textarea
                          key={activeSection}
                          value={currentContent}
                          onChange={(e) => handleContentChange(e.target.value)}
                          placeholder={SECTION_PLACEHOLDERS[activeSection]}
                          rows={activeSection === "headline" ? 3 : 12}
                          className="w-full px-4 py-3 bg-transparent text-sm text-foreground font-inter placeholder:text-muted-foreground focus:outline-none resize-none"
                        />
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          </>
          )}

          {activeTab === "coverLetter" && (
          <>
          <p className="text-sm text-muted-foreground font-inter -mt-2 mb-6">
            Select a cover letter and tailor it for the job description above — grounded in whichever CV is selected on the CV Enhancement tab, if any. Preview-only, never alters the saved cover letter.
          </p>

            <div className="rounded-2xl border border-border bg-surface/50 p-5 mb-6 space-y-4">
              <div>
                <label className="block text-xs font-medium text-muted-foreground font-inter mb-1.5">
                  Select Cover Letter
                </label>
                {loadingCoverLetterDocs ? (
                  <div className="flex items-center gap-2 py-2">
                    <Spinner size="sm" />
                    <span className="text-sm text-muted-foreground font-inter">Loading cover letters…</span>
                  </div>
                ) : coverLetterDocs.length === 0 ? (
                  <p className="text-sm text-muted-foreground font-inter">
                    No cover letters found. Create one in{" "}
                    <Link to={`${cvApiBase()}/cover-letters`} className="underline hover:text-primary">My Cover Letters</Link>{" "}first.
                  </p>
                ) : (
                  <AppSelect value={String(selectedCoverLetterId)} onChange={handleCoverLetterDocChange}>
                    <option value="">— Choose a cover letter —</option>
                    {coverLetterDocs.map((doc) => (
                      <option key={doc.id} value={doc.id}>{doc.title}</option>
                    ))}
                  </AppSelect>
                )}
              </div>

              {selectedCoverLetterId !== "" && (
                <div className="flex items-center justify-between py-1">
                  <div className="flex items-center gap-2.5">
                    <button
                      onClick={() => handleToggleClPrevious(!clShowPrevious)}
                      disabled={clLoadingPrevious || clEnhancing}
                      className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${
                        clShowPrevious ? "bg-primary" : "bg-border"
                      } disabled:opacity-50`}
                      role="switch"
                      aria-checked={clShowPrevious}
                    >
                      <span
                        className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow transition-transform duration-200 ${
                          clShowPrevious ? "translate-x-4" : "translate-x-0"
                        }`}
                      />
                    </button>
                    <span className="text-xs text-muted-foreground font-inter">
                      {clLoadingPrevious
                        ? "Loading…"
                        : clShowPrevious
                        ? "Showing previous enhancement"
                        : "Load previous enhancement"}
                    </span>
                  </div>
                  {coverLetterDocs.find((d) => d.id === selectedCoverLetterId)?.last_enhanced_at && (
                    <span className="text-xs text-muted-foreground font-inter">
                      Last: {coverLetterDocs.find((d) => d.id === selectedCoverLetterId)!.last_enhanced_at}
                    </span>
                  )}
                </div>
              )}

              {clNoPreviousFound && (
                <p className="text-xs text-muted-foreground font-inter">
                  No previous enhancement saved for this cover letter.
                </p>
              )}

              {clError && (
                <p className="text-sm text-red-400 font-inter">{clError}</p>
              )}

              <div className="flex items-center justify-end">
                <button
                  onClick={handleEnhanceCoverLetter}
                  disabled={!selectedCoverLetterId || !jobDescription.trim() || clEnhancing || clShowPrevious}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed text-primary-foreground text-sm font-medium font-inter rounded-xl transition-colors"
                >
                  {clEnhancing ? <Spinner size="sm" /> : <Sparkles className="w-4 h-4" />}
                  {clEnhancing ? "Tailoring…" : "Enhance Cover Letter"}
                </button>
              </div>
            </div>

            {clResult && (
              <div>
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="flex items-center gap-3">
                      <h2 className="text-base font-semibold text-foreground font-inter">
                        Enhanced Result
                      </h2>
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs text-muted-foreground font-inter">Score</span>
                        <ScoreBadge score={clResult.overall_score} size="md" />
                      </div>
                    </div>
                    {clResult.top_priority_fix && clResult.top_priority_fix !== "none" && (
                      <p className="text-xs text-muted-foreground font-inter mt-1">
                        Top fix: <span className="text-muted-foreground">{clResult.top_priority_fix}</span>
                      </p>
                    )}
                    {clResult.critique && clResult.critique !== "none" && (
                      <p className="text-xs text-muted-foreground font-inter mt-1">
                        Critique: <span className="text-muted-foreground">{clResult.critique}</span>
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground font-inter mt-1">
                      Edit below — changes are preview-only unless saved as a new cover letter.
                    </p>
                    {saveClMessage && (
                      <p className="text-xs text-emerald-400 font-inter mt-1">{saveClMessage}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => setShowSaveClModal(true)}
                      className="inline-flex items-center gap-2 px-3 py-2 bg-surface-muted hover:bg-surface-muted border border-border text-foreground text-sm font-medium font-inter rounded-xl transition-colors"
                    >
                      <Save className="w-4 h-4" />
                      Save as new
                    </button>
                    <button
                      onClick={handleClPrint}
                      className="inline-flex items-center gap-2 px-3 py-2 bg-surface-muted hover:bg-surface-muted border border-border text-foreground text-sm font-medium font-inter rounded-xl transition-colors"
                    >
                      <Printer className="w-4 h-4" />
                      Download / Print
                    </button>
                  </div>
                </div>
                <textarea
                  value={clLocalContent}
                  onChange={(e) => setClLocalContent(e.target.value)}
                  rows={14}
                  className="w-full px-4 py-3 bg-surface/50 border border-border rounded-xl text-sm text-foreground font-inter placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                />
              </div>
            )}
          </>
          )}
        </div>
        {/* Right column: log panel */}
        {showLogs && (
          <div className="w-96 shrink-0 sticky top-20" /* 64px fixed top bar + 16px */>
            <LogPanel logs={logs} logsEndRef={logsEndRef} enhancing={enhancing} provider={runProvider} tokenUsage={tokenUsage} />
          </div>
        )}
      </div>
      {showComparison && originalDoc && enhancedDoc && (
        <ComparisonModal
          originalDoc={originalDoc}
          enhancedDoc={enhancedDoc}
          jobDescription={jobDescription}
          onClose={handleCloseComparison}
        />
      )}
      {showSaveCvModal && (
        <SaveAsNewCvModal
          defaultTitle={`${originalDoc?.title ?? "CV"} — Enhanced`}
          onSave={handleSaveCvAsNew}
          onClose={() => setShowSaveCvModal(false)}
        />
      )}
      {showSaveClModal && (
        <SaveAsNewCoverLetterModal
          defaultTitle={`${coverLetterDocs.find((d) => d.id === selectedCoverLetterId)?.title ?? "Cover Letter"} — Enhanced`}
          onSave={handleSaveClAsNew}
          onClose={() => setShowSaveClModal(false)}
        />
      )}
    </div>
  );
}
